import { redirect } from 'next/navigation'
import type { TextPart } from 'ai'

import type { Attachment, ChatMessage } from './ai/ai-types'

export { fetchWithErrorHandlers } from './utils/fetch-with-error-handlers'
export { cn } from 'cn'

/**
 * Formats a date into a string representation.
 *
 * @param input - The date to format. It can be a string, number, or Date object.
 * @returns The formatted date as a string.
 */
export function formatDateToHuman(input: string | number | Date): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export function generateReportRef(format: string, from: string): string {
  const year = from.slice(0, 4)
  const suffix = format === 'law-society' ? 'LS' : 'PI'
  const seq = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, '0')
  return `PAI-${year}-${suffix}-${seq}`
}

export const getStringFromBuffer = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

/**
 * Converts a File object to a base64 encoded Data URL string.
 * Useful for previewing images or preparing files for upload.
 *
 * @param file - The File object to convert (e.g., from file input or drag-and-drop)
 * @returns Promise that resolves with the file's contents as a base64 Data URL
 * @throws Rejects with FileReader error if reading fails
 *
 * Example Data URL format:
 * data:[<mediatype>][;base64],<data>
 */
export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Create FileReader instance to read file contents
    const reader = new FileReader()

    // Resolve promise with the base64 string when file is successfully read
    reader.onload = () => resolve(reader.result as string)

    // Reject promise if there's an error reading the file
    reader.onerror = (error) => reject(error)

    // Begin reading the file as Data URL (base64 encoded)
    reader.readAsDataURL(file)
  })
}

/**
 * Generates an array of attachment objects from an array of File objects.
 * Each attachment object contains the file's name, content type, and a base64 encoded Data URL.
 *
 * @param fileAttachments - An array of File objects to be processed.
 * @returns A promise that resolves to an array of Attachment objects.
 */
export const generateAttachments = async (
  fileAttachments: File[],
): Promise<Attachment[]> => {
  const attachments: Attachment[] = [] // Initialize an empty array to hold the attachment objects.

  // Iterate over each file in the provided fileAttachments array.
  for (const file of fileAttachments) {
    // Check if the file has a valid type before processing.
    if (file.type) {
      // Convert the file to a base64 encoded Data URL.
      const dataUrl = await readFileAsDataURL(file)
      // Push a new attachment object into the attachments array.
      attachments.push({
        name: file.name, // The name of the file.
        contentType: file.type, // The MIME type of the file.
        url: dataUrl, // The base64 encoded Data URL of the file.
      })
    }
  }

  return attachments // Return the array of attachment objects.
}

/**
 * Parses embedding data ensuring proper type conversion from string to array
 * @param embedding - Raw embedding data that might be a string or already parsed
 * @returns Parsed embedding data
 */
export function parseEmbedding<T>(embedding: string | T): T {
  return typeof embedding === 'string' ? JSON.parse(embedding) : embedding
}

// Define an interface for the processed attachment structure
interface ProcessedAttachment {
  parsedContent: Array<{
    content: string
  }>
}

/**
 * Extracts and concatenates content from a processed attachment's parsed content array.
 *
 * @param processedAttachment - An object containing parsed content from an attachment
 * @returns A string containing all content items joined with double newlines, or empty string if invalid input
 */
export function extractContentAsString(
  processedAttachment: ProcessedAttachment | null | undefined,
): string {
  // Guard clause: Return empty string if attachment is null/undefined or parsedContent is not an array
  if (
    !processedAttachment ||
    !Array.isArray(processedAttachment.parsedContent)
  ) {
    return ''
  }

  // Extract content from each item in parsedContent array and join with double newlines
  return processedAttachment.parsedContent
    .map((item) => item.content)
    .join('\n\n')
}

/**
 * Redirects to a specified path with an encoded message as a query parameter.
 * @param {('error' | 'success')} type - The type of message, either 'error' or 'success'.
 * @param {string} path - The path to redirect to.
 * @param {string} message - The message to be encoded and added as a query parameter.
 * @returns {never} This function doesn't return as it triggers a redirect.
 */
export function encodedRedirect(
  type: 'error' | 'success',
  path: string,
  message: string,
) {
  return redirect(`${path}?${type}=${encodeURIComponent(message)}` as any)
}

/**
 * Custom error interface extending the base Error type for API requests
 * Adds additional properties to track API error details:
 * - info: JSON response from the API containing error details
 * - status: HTTP status code from the response
 */
interface ApplicationError extends Error {
  info: string
  status: number
}

/**
 * Generic data fetching utility for making HTTP requests
 *
 * @param url - The URL to fetch data from
 * @returns Promise resolving to the JSON response data
 * @throws ApplicationError with enhanced error details if the request fails
 *
 * Key features:
 * - Automatically parses JSON responses
 * - Enhanced error handling with status codes and response info
 * - TypeScript support for error typing
 */
export const fetcher = async (url: string) => {
  // Make the HTTP request
  const res = await fetch(url)

  // Check if the response indicates an error (non-200 status code)
  if (!res.ok) {
    // Create and enhance error object with additional context
    const error = new Error(
      'An error occurred while fetching the data.',
    ) as ApplicationError

    // Attach the parsed error response and status code
    error.info = await res.json()
    error.status = res.status

    throw error
  }

  // Parse and return the successful response
  return res.json()
}

// Define Document interface for timestamp retrieval
interface Document {
  id: string
  title: string
  kind: string
  content: string
  createdAt: Date
  userId: string
}

export function getDocumentTimestampByIndex(
  documents: Array<Document> | undefined | null,
  index: number,
) {
  if (!documents || !documents.length) return new Date()
  if (index >= documents.length) return new Date()

  return documents[index]?.createdAt || new Date()
}

/**
 * Creates a promise that resolves after a specified number of milliseconds.
 * @param ms The number of milliseconds to delay.
 * @returns A promise that resolves after the delay.
 */
export function delay(ms: number): Promise<void> {
  // Create a new Promise. The constructor takes a function (executor) with 'resolve' as an argument.
  return new Promise((resolve) =>
    // setTimeout executes the provided function (in this case, 'resolve') once after the specified delay (ms).
    // When 'resolve()' is called, the Promise is fulfilled.
    setTimeout(resolve, ms),
  )
}

/**
 * Formats a number with commas as thousands separators
 * @param num - The number to format
 * @returns Formatted number string with commas
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-IE').format(num)
}

/**
 * Formats a file size in bytes to a human-readable string
 * @param bytes - The file size in bytes
 * @returns A formatted string (e.g., "1.5 KB", "3.2 MB")
 */
export function bytesToSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))

  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Generates a RFC 4122 v4 UUID string.
 *
 * Strategy:
 * - Prefer the built-in `crypto.randomUUID()` for strong randomness and performance
 * - Fallback to a Math.random-based implementation when `crypto` is unavailable
 *
 * Notes:
 * - `crypto.randomUUID()` is available in modern browsers and Node 16.17+
 * - The fallback preserves the v4 structure, suitable for non-cryptographic use
 */
export function generateUUID(): string {
  // Use native secure UUID generation when available
   
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  // Fallback for environments without `crypto.randomUUID()`
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Validates basic UUID format for database query safety.
 *
 * Since we control UUID generation with our `generateUUID()` function,
 * this provides a simple format check to prevent malformed IDs from
 * reaching database queries and causing errors.
 *
 * @param value - The string to validate as a UUID
 * @returns true if the value matches basic UUID format, false otherwise
 *
 * @example
 * ```ts
 * isUUID('550e8400-e29b-41d4-a716-446655440000') // true
 * isUUID('invalid-uuid') // false
 * isUUID(null) // false
 * ```
 */
export function isUUID(value: string | undefined | null): boolean {
  if (typeof value !== 'string') return false
  if (value.length !== 36) return false

  // Simple regex check for basic UUID format: 8-4-4-4-12 hex characters
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  )
}

export function getTextContentFromMessage(message: ChatMessage): string {
  return message.parts
    .filter<TextPart>((part) => part.type === 'text')
    .map((part) => part.text)
    .join('')
}

/**
 * Deduplicates text parts in a message to prevent copy duplication
 * Returns only the first text part's content
 */
export function getSingleTextContentFromMessage(message: ChatMessage): string {
  const textPart = message.parts.find((part) => part.type === 'text')
  return textPart?.text ?? ''
}

/**
 * Deduplicates message parts by removing duplicate text parts
 * Keeps only the first text part if multiple exist
 */
export function deduplicateMessageParts(
  parts: ChatMessage['parts'],
): ChatMessage['parts'] {
  const fileParts = parts.filter((part) => part.type === 'file')
  const textParts = parts.filter((part) => part.type === 'text')
  const otherParts = parts.filter(
    (part) => part.type !== 'text' && part.type !== 'file',
  )

  // Keep only the first text part to prevent duplication
  const firstTextPart = textParts[0]

  return [
    ...fileParts,
    ...(firstTextPart ? [firstTextPart] : []),
    ...otherParts,
  ]
}
